import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { Button, FormField } from './ui';
import NumberInput from './NumberInput';
import { toast } from './Toast';

// Stok kartında "Paket / Bundle" sekmesi.
// Bir bundle SKU'nun bileşenlerini tanımlar. Satışta bu paket barkodu okutulunca
// bileşenlerine patlar (her biri kendi stoğundan düşer, KDV'si korunur).
// Kendi Kaydet butonu vardır — ana stok kaydından bağımsız PUT /stock-bundles/:id/items.
export default function BundleSection({ stockId, isNew, isEditing }) {
  const [isBundle, setIsBundle] = useState(false);
  const [display, setDisplay] = useState('explode');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [prodSearch, setProdSearch] = useState('');
  const [prodResults, setProdResults] = useState([]);
  const [previewPrice, setPreviewPrice] = useState('');
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    if (!stockId) return;
    setLoading(true);
    try {
      const r = await api.get(`/stock-bundles/${stockId}/items`);
      if (r.data.success) {
        const its = (r.data.data?.items || []).map(c => ({
          component_stock_id: c.component_stock_id, stock_code: c.stock_code, stock_name: c.stock_name,
          quantity: c.quantity, price_alloc_pct: c.price_alloc_pct, vat_rate: c.vat_rate,
        }));
        setItems(its);
        setIsBundle(its.length > 0);
      }
    } catch (_) { /* yeni veya bundle değil */ }
    finally { setLoading(false); }
  }, [stockId]);

  useEffect(() => { load(); }, [load]);

  const searchProducts = async (q) => {
    setProdSearch(q);
    if (!q || q.length < 2) { setProdResults([]); return; }
    try {
      const r = await api.get('/stocks', { params: { search: q, limit: 10, is_active: 'yes' } });
      if (r.data.success) setProdResults((r.data.data?.items || r.data.data || []).filter(p => String(p.id) !== String(stockId)));
    } catch (_) { setProdResults([]); }
  };
  const addItem = (p) => {
    if (items.some(i => String(i.component_stock_id) === String(p.id))) return;
    setItems([...items, { component_stock_id: p.id, stock_code: p.code, stock_name: p.name, quantity: 1, price_alloc_pct: null, vat_rate: p.vat_rate }]);
    setProdSearch(''); setProdResults([]);
  };
  const upd = (idx, k, v) => setItems(items.map((it, i) => i === idx ? { ...it, [k]: v } : it));
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        is_bundle: isBundle && items.length > 0,
        bundle_display: display,
        items: items.map(i => ({
          component_stock_id: i.component_stock_id,
          quantity: parseFloat(i.quantity) || 1,
          price_alloc_pct: (i.price_alloc_pct === '' || i.price_alloc_pct == null) ? null : parseFloat(i.price_alloc_pct),
        })),
      };
      const r = await api.put(`/stock-bundles/${stockId}/items`, payload);
      if (r.data.success) { toast.success('Paket bileşenleri kaydedildi'); setIsBundle(r.data.data.is_bundle); }
    } catch (err) { toast.error(err.response?.data?.message || 'Kayıt başarısız'); }
    finally { setSaving(false); }
  };

  const doPreview = async () => {
    try {
      const r = await api.get(`/stock-bundles/${stockId}/preview`, { params: { quantity: 1, unit_price: parseFloat(previewPrice) || 0 } });
      if (r.data.success) setPreview(r.data.data.lines || []);
    } catch (_) { setPreview([]); }
  };

  if (isNew || !stockId) {
    return <div style={{ padding: 16, fontSize: 13, color: 'var(--text-secondary)' }}>
      Paket bileşenlerini tanımlamak için önce ürünü kaydedin.
    </div>;
  }

  const ro = !isEditing;
  const hasAlloc = items.some(i => i.price_alloc_pct != null && i.price_alloc_pct !== '');

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{ padding: 12, borderRadius: 8, background: 'rgba(59,130,246,.07)', border: '1px solid rgba(59,130,246,.2)', fontSize: 12.5, color: 'var(--text-secondary)' }}>
        📦 <b>Paket (Bundle):</b> Bu ürün satışta aşağıdaki bileşenlerine patlar; her bileşen kendi stoğundan düşer ve kendi KDV oranını korur.
        Paketin satış fiyatı bileşenlere liste fiyatına orantılı dağıtılır (veya aşağıda manuel % belirleyin).
      </div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <FormField label="Bu ürün bir paket mi?">
          <select value={isBundle ? '1' : '0'} onChange={e => setIsBundle(e.target.value === '1')} disabled={ro}>
            <option value="0">Hayır (normal ürün)</option>
            <option value="1">Evet (paket)</option>
          </select>
        </FormField>
        <FormField label="Fişte gösterim">
          <select value={display} onChange={e => setDisplay(e.target.value)} disabled={ro}>
            <option value="explode">Bileşenler ayrı satır</option>
            <option value="single">Tek satır (arkada düş)</option>
          </select>
        </FormField>
      </div>

      {isBundle && (
        <>
          {!ro && (
            <div style={{ position: 'relative' }}>
              <input value={prodSearch} onChange={e => searchProducts(e.target.value)} placeholder="Bileşen ürün ara (kod/ad/barkod)…" />
              {prodResults.length > 0 && (
                <div style={{ position: 'absolute', zIndex: 20, background: 'var(--card-bg,#fff)', border: '1px solid var(--border)', borderRadius: 8, width: '100%', maxHeight: 220, overflow: 'auto', boxShadow: '0 6px 20px rgba(0,0,0,.15)' }}>
                  {prodResults.map(p => (
                    <div key={p.id} onClick={() => addItem(p)} style={{ padding: '8px 10px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid var(--border)' }}>
                      <b>{p.code}</b> — {p.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--text-secondary)', fontSize: 12 }}>
                <th style={{ padding: 6 }}>Bileşen</th>
                <th style={{ padding: 6, width: 110 }}>Adet</th>
                <th style={{ padding: 6, width: 130 }}>Dağıtım % <span title="Boş bırakılırsa liste fiyatına orantılı otomatik dağıtılır">ⓘ</span></th>
                <th style={{ padding: 6, width: 70 }}>KDV</th>
                <th style={{ padding: 6, width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <tr key={it.component_stock_id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: 6 }}><b>{it.stock_code}</b> {it.stock_name}</td>
                  <td style={{ padding: 6 }}><NumberInput value={it.quantity} onChange={v => upd(idx, 'quantity', v)} decimals={3} disabled={ro} /></td>
                  <td style={{ padding: 6 }}><NumberInput value={it.price_alloc_pct ?? ''} onChange={v => upd(idx, 'price_alloc_pct', v)} decimals={2} disabled={ro} /></td>
                  <td style={{ padding: 6 }}>%{it.vat_rate ?? 0}</td>
                  <td style={{ padding: 6 }}>{!ro && <span onClick={() => removeItem(idx)} style={{ cursor: 'pointer', color: '#dc2626' }}>✕</span>}</td>
                </tr>
              ))}
              {!items.length && <tr><td colSpan={5} style={{ padding: 10, color: 'var(--text-secondary)' }}>Bileşen eklenmedi</td></tr>}
            </tbody>
          </table>

          {hasAlloc && (
            <div style={{ fontSize: 11.5, color: '#d97706' }}>
              ⚠ Manuel dağıtım yüzdeleri toplamı %100 olmalı (aksi halde orantılanır).
            </div>
          )}

          {/* Önizleme */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', borderTop: '1px dashed var(--border)', paddingTop: 10 }}>
            <FormField label="Önizleme: paket fiyatı (KDV hariç)">
              <NumberInput value={previewPrice} onChange={setPreviewPrice} decimals={2} />
            </FormField>
            <Button variant="ghost" size="sm" onClick={doPreview} disabled={!items.length}>Dağılımı göster</Button>
          </div>
          {preview && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <tbody>
                {preview.map((l, i) => (
                  <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: 5 }}>{l.stock_code} {l.stock_name}</td>
                    <td style={{ padding: 5, textAlign: 'right' }}>{l.quantity} ad</td>
                    <td style={{ padding: 5, textAlign: 'right' }}>{l.unit_price} TL/ad</td>
                    <td style={{ padding: 5, textAlign: 'right' }}>= {(+(l.unit_price * l.quantity).toFixed(2))} TL</td>
                    <td style={{ padding: 5, textAlign: 'right', color: 'var(--text-secondary)' }}>KDV%{l.vat_rate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {!ro && <div><Button onClick={save} disabled={saving}>{saving ? 'Kaydediliyor…' : '💾 Paketi Kaydet'}</Button></div>}
        </>
      )}
    </div>
  );
}
